// LIBRERIE/IMPORTS
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

// COMPONENTI
import NotFoundProperty from '../Error/NotFoundProperty';

// CONFIGURAZIONI & TIPI
import { Property } from '../../types/property';
import appConfig from '../../config/appConfig';
import { tipologieTranslations, Translations } from '../../config/i18n/propertyTranslations';
import { contractTranslations } from '../../config/i18n/elementsMappingProperty';


interface ImmobiliListProps {
    properties: Property[];
    loading: boolean;
    error: any;
    basePath: string;
    labelSold: boolean;
}

const ImmobiliList: React.FC<ImmobiliListProps> = ({ properties, loading, error, basePath, labelSold }) => {
    const { t, i18n } = useTranslation();
    const currentLanguage = i18n.language as keyof Translations;;


    if (!loading && !error && properties.length === 0) {
        return <NotFoundProperty />;
    }

    const getPropertyName = (property: Property) => {
        const propertyType = tipologieTranslations.find(tipo => tipo.id === property.tipologyDetails.id)?.translations[currentLanguage];
        const contractType = contractTranslations.find(contract => contract.id === property.contractDetails.id)?.translations[currentLanguage];
        const locationName = property.addressDetails.quartiereName || property.addressDetails.frazioneName || property.addressDetails.comuneName;
        const contractLocationConnection = ["centro", "centro storico"].includes(locationName) ? t('common.propertyContract-in-propertyLocation') : t('common.propertyContract-in-propertyLocation2');
        return propertyType + " " + t('common.propertyType-in-propertyContract') + " " +  contractType + " " + contractLocationConnection +" " + locationName;
    };

    const langPrefix = appConfig.integrations.multilang ? `/${i18n.language}` : '';

    return (
        <>
            {properties.map((property: Property) => {
                const propertyName = getPropertyName(property);
                const propertyUrl = `${langPrefix}/${basePath}/${property.id}`;
                return (
                    <Link to={propertyUrl} className="property-card-list" key={property.id}>
                        <div className="container-fluid">
                            <div className="row">
                                <div className="col-12 col-lg-4">
                                    <div className="img-wrapper">
                                        <img 
                                            src={property.mainImage ? property.mainImage : '/assets/images/logo-gold.png'} 
                                            alt={(t('property.card.imageAltBase', 'Immagine proprietà') as string) + ` ${propertyName}`} 
                                            style={{ objectFit: property.mainImage ? 'cover' : 'contain', width: property.mainImage ? '100%' : '70%', margin: "auto" }} />
                                        {labelSold ? (
                                            <div className="property-label">
                                                <span className="badge badge-success">
                                                    VENDUTO DA NOI
                                                </span>
                                            </div>
                                        ) : (
                                            <div className="property-label">
                                                {property.contractDetails?.name && (
                                                    <span className="badge">
                                                        {t(`property.contractTypes.${property.contractDetails.name}`, property.contractDetails.name) as string}
                                                    </span>
                                                )}
                                                {property.auctionDetails?.auction && (
                                                    <span className="badge ml-2">
                                                        {t('property.auction')}
                                                    </span>
                                                )}
                                            </div>
                                        )}

                                        {property.exclusive && (
                                            <div className="exclusive-box">
                                                <span className="exclusive-text">{t('property.exclusive', 'Esclusiva') as string}</span>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                <div className="col-12 col-lg-8">
                                    <div>
                                        <h5 className="property-title mb-2">
                                            {propertyName}
                                        </h5>
                                        <div className="property-price mb-2">
                                            {property.priceDetails.reservedNegotiation ? (
                                                // CASO 1: Trattativa riservata
                                                <span className="reserved">{t('property.priceReserved', 'Tr. Riservata') as string}</span>
                                            ) : property.auctionDetails.auction ? (
                                                // CASO 2: Immobile in asta
                                                <>
                                                    da € {new Intl.NumberFormat('it-IT').format(property.priceDetails.price)}
                                                </>
                                            ) : (
                                                // CASO 3: Prezzo standard (con o senza negoziabilità)
                                                <>
                                                    € {new Intl.NumberFormat('it-IT').format(property.priceDetails.price)}
                                                    {property.priceDetails.negotiable && (
                                                        <span className="negotiable">{`(${(t('common.negotiable', 'Tr.') as string)})`}</span>
                                                    )}
                                                </>
                                            )}
                                        </div>
                                        <div className="property-reference">
                                            <span className="ref-label">{(t('property.reference', 'Rif.') as string)}:</span> {property.ref}
                                        </div>
                                    </div>

                                    <div>
                                        {property?.shortDetails?.swimmingPool && (
                                            <span className="badge badge-primary mr-2 mb-2">
                                                {t('property.swimmingPool') as string}
                                            </span>
                                        )}

                                        <div className="d-flex justify-content-between align-items-center">

                                            <ul className="list-unstyled d-flex mb-0">
                                                {property.rooms != null && property.rooms !== '0' && (
                                                    <li className="mr-3">
                                                        <i className="fas fa-bed mr-2"></i>
                                                        <span>{property.rooms}</span>
                                                    </li>
                                                )}
                                                {property.bathrooms != null && property.bathrooms !== 0 && (
                                                    <li className="mr-3">
                                                        <i className="fas fa-bath mr-2"></i>
                                                        <span>{property.bathrooms}</span>
                                                    </li>
                                                )}
                                                {property.sqmt && property.sqmt !== 0 && (
                                                    <li className="mr-3">
                                                        <i className="fas fa-vector-square mr-2"></i>
                                                        <span>{property.sqmt} m²</span>
                                                    </li>
                                                )}
                                            </ul>

                                            <div>
                                                <span className="property-location text-uppercase pr-4">
                                                    <i className="fas fa-map-marker-alt mr-2"></i>
                                                    {property.addressDetails.frazioneName || property.addressDetails.comuneName}
                                                </span>
                                                <span className="text-uppercase">
                                                    <i className="fas fa-home mr-2"></i>
                                                    {property.tipologyDetails.name}
                                                </span>
                                            </div>

                                        </div>

                                    </div>
                                </div>
                            </div>
                        </div>
                    </Link>
                );
            })}
        </>
    );
};

export default ImmobiliList;